// Plan v2 update 1 — "An Klient senden" dialog. Lets agent pick e-mail or
// SMS, edit subject/body, and send the rendered PDF (plus WKO when document
// type warrants it). Used both before and after signature.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare, MessageCircle, Paperclip } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  type DocumentRow,
  useSendDocumentToClient,
} from '@/hooks/data/useDocuments';
import { apiFetch } from '@/lib/api';
import type { ClientRow } from '@/hooks/data/useClients';

interface Props {
  document: DocumentRow;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const TYPE_TITLE: Record<DocumentRow['type'], string> = {
  vermittlungsauftrag: 'Allein-Vermittlungsauftrag',
  besichtigungsschein: 'Besichtigungsschein',
  kaufanbot: 'Befristetes Kaufanbot',
};

/** Migration 0058 — the e-mail subject the client reads must name the contract
 *  they are actually getting. Only an explicit 'einfach' changes the wording;
 *  every pre-0058 document is exclusive. */
function documentTitle(doc: DocumentRow): string {
  if (doc.type === 'vermittlungsauftrag' && doc.contract_variant === 'einfach') {
    return 'Vermittlungsauftrag';
  }
  return TYPE_TITLE[doc.type];
}

export function SendToClientDialog({ document, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const send = useSendDocumentToClient();
  const [client, setClient] = useState<ClientRow | null>(null);
  const [channel, setChannel] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientOverride, setRecipientOverride] = useState('');

  const includeWKO =
    document.type === 'vermittlungsauftrag' || document.type === 'kaufanbot';
  const docTitle = documentTitle(document);

  useEffect(() => {
    if (!open) return;
    setSubject(`Ihr ${docTitle} Nr. ${document.number} — bitte prüfen`);
    setBody('');
    setChannel('email');
    setClient(null);
    setRecipientOverride('');

    if (document.client_id) {
      apiFetch(`/clients/${document.client_id}`)
        .then((r) => (r.ok ? (r.json() as Promise<ClientRow>) : null))
        .then((c) => setClient(c))
        .catch(() => {});
    }
  }, [open, document.id, document.number, docTitle, document.client_id]);

  // Email channel pulls client.email; SMS + WhatsApp both use phone.
  const recipientFromClient =
    client && (channel === 'email' ? client.email ?? '' : client.phone);
  const finalRecipient = recipientOverride.trim() || recipientFromClient || '';

  const fullName = client
    ? [client.title, client.first_name, client.last_name].filter(Boolean).join(' ')
    : '—';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalRecipient) {
      toast.error(t('docs.send.no_recipient', 'Empfänger fehlt.'));
      return;
    }
    try {
      const res = await send.mutateAsync({
        id: document.id,
        channel,
        subject: subject.trim() || undefined,
        body: body.trim() || undefined,
        recipientOverride: recipientOverride.trim() || undefined,
      });
      const msg =
        channel === 'email'
          ? t('docs.send.email_done', 'E-Mail an Klient gesendet.')
          : channel === 'whatsapp'
            ? t('docs.send.whatsapp_done', 'WhatsApp-Link an Klient gesendet.')
            : t('docs.send.sms_done', 'SMS-Link an Klient gesendet.');
      toast.success(msg, {
        description: res.view_url
          ? `${t('docs.send.view_link', 'View-Link')}: ${res.view_url}`
          : undefined,
        duration: 8000,
      });
      onOpenChange(false);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Send className="h-4 w-4" />
            {t('docs.send.title', 'An Klient senden')}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onSubmit}>
          {/* Document summary */}
          <div className="rounded-md border border-line bg-paper-2 px-3 py-2 text-sm">
            <p className="font-medium text-ink">
              {docTitle}{' '}
              <span className="font-mono text-ink-3">Nr. {document.number}</span>
            </p>
            <p className="text-xs text-ink-3">
              {document.signed
                ? t('docs.send.signed_label', 'Unterschrieben')
                : t('docs.send.unsigned_label', 'Noch nicht unterschrieben')}
            </p>
          </div>

          {/* Recipient */}
          <div className="rounded-md border border-line bg-paper-2 px-3 py-2">
            <p className="text-xs uppercase text-ink-3 tracking-[0.08em]">
              {t('docs.send.recipient', 'Empfänger')}
            </p>
            <p className="text-sm text-ink">{fullName}</p>
            {finalRecipient ? (
              <p className="font-mono text-xs text-ink-2">{finalRecipient}</p>
            ) : (
              <p className="text-xs text-[hsl(var(--danger))]">
                {channel === 'email'
                  ? t('docs.send.no_email', 'Keine E-Mail-Adresse hinterlegt.')
                  : t('docs.send.no_phone', 'Keine Telefonnummer hinterlegt.')}
              </p>
            )}
          </div>

          {/* Channel */}
          <div className="space-y-1.5">
            <Label>{t('docs.send.channel', 'Versandweg')}</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setChannel('email')}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                  channel === 'email'
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-card text-ink-2 hover:bg-paper-2'
                }`}
              >
                <Mail className="h-3.5 w-3.5" />
                E-Mail
              </button>
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                  channel === 'sms'
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-card text-ink-2 hover:bg-paper-2'
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                SMS
              </button>
              <button
                type="button"
                onClick={() => setChannel('whatsapp')}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                  channel === 'whatsapp'
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-card text-ink-2 hover:bg-paper-2'
                }`}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                WhatsApp
              </button>
            </div>
            {channel === 'whatsapp' && (
              <p className="text-[11px] text-ink-3">
                {t(
                  'docs.send.whatsapp_hint',
                  'WhatsApp-Nachricht mit View-Link an die hinterlegte Telefonnummer. PDF nicht direkt anhängbar im Sandbox; Link öffnet das Dokument im Browser.',
                )}
              </p>
            )}
          </div>

          {/* Recipient override */}
          <div className="space-y-1.5">
            <Label>
              {channel === 'email'
                ? t('docs.send.email_override', 'E-Mail-Adresse (optional)')
                : t('docs.send.phone_override', 'Telefonnummer (optional)')}
            </Label>
            <Input
              value={recipientOverride}
              onChange={(e) => setRecipientOverride(e.target.value)}
              placeholder={recipientFromClient || (channel === 'email' ? 'name@beispiel.at' : '+43 660 …')}
              type={channel === 'email' ? 'email' : 'tel'}
            />
          </div>

          {/* Subject (email only) */}
          {channel === 'email' && (
            <div className="space-y-1.5">
              <Label>{t('docs.send.subject', 'Betreff')}</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          )}

          {/* Message body */}
          <div className="space-y-1.5">
            <Label>
              {channel === 'email'
                ? t('docs.send.body_email', 'Nachricht')
                : t('docs.send.body_sms', 'Begleittext (optional)')}
            </Label>
            <Textarea
              rows={channel === 'email' ? 6 : 3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={
                channel === 'email'
                  ? t(
                      'docs.send.body_email_hint',
                      'Leer lassen für Standard-Vorlage „Sehr geehrte/r …"',
                    )
                  : t(
                      'docs.send.body_sms_hint',
                      'Leer lassen für Standard-SMS mit Link.',
                    )
              }
            />
          </div>

          {/* Attachment preview */}
          <div className="rounded-md border border-line bg-paper-2 p-3">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-ink-3">
              <Paperclip className="me-1 inline h-3 w-3" />
              {channel === 'email'
                ? t('docs.send.attachments', 'Anhänge')
                : t('docs.send.links', 'Links')}
            </p>
            <ul className="space-y-1 text-xs">
              <li className="text-ink-2">
                1. <span className="font-mono">{docTitle}-{document.number}.pdf</span>{' '}
                <span className="text-ink-3">
                  ({t('docs.send.main_doc', 'Hauptdokument')})
                </span>
              </li>
              {includeWKO && (
                <li className="text-ink-2">
                  2. <span className="font-mono">WKO_Nebenkosten_Kauf.pdf</span>{' '}
                  <span className="text-ink-3">
                    ({t('docs.send.wko', 'Pflichtbeilage')})
                  </span>
                </li>
              )}
            </ul>
            {channel === 'sms' && (
              <p className="mt-2 text-[11px] text-ink-3">
                {t(
                  'docs.send.sms_note',
                  'Per SMS erhält der Klient einen 7-Tage View-Link. Der Klient kann das PDF auf seinem Telefon öffnen.',
                )}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={send.isPending || !finalRecipient}>
              <Send className="me-1 h-3.5 w-3.5" />
              {send.isPending
                ? t('common.sending', 'Sendet …')
                : t('docs.send.cta', 'Senden')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
